import React, { useState, useEffect, Fragment, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import Label from '../../../../../Label';
import useAssetImageUpload from '../../../../../../../hooks/useAssetImageUpload';
import { CARD_ELEMENT_TYPE } from '../../../../../../../services/cards/constants';
import { getImageBasedResizedFrame } from '../../../CardDesignerCardElements/CardDesignerCardElementControls/components/CardDesignerCardElementResizeControls/helpers';
import Button from '../../../../../Button';
import UploadIcon from '../../../../../../icons/UploadIcon';
import useAccountLogo from './hooks/useAccountLogo';
import { MAX_Z_INDEX } from '../../../../helpers';
import { BASE_LOGO_FRAME } from './helpers';
import useAccountPrintAssets from '../../../../../../../apps/pro/views/Account/AccountPrintAssets/components/AccountPrintAsset/hooks/useAccountPrintAssets';
import { PRINT_ASSET_TYPES } from '../../../../../../../constants/print-assets';
import useDebounce from '../../../../../../../hooks/useDebounce';
import FormGroup from '../../../../../FormGroup';

const propTypes = {
  className: PropTypes.string,
  cardBounds: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number,
    x: PropTypes.number,
    y: PropTypes.number,
  }),
  element: PropTypes.shape({
    cardElement: PropTypes.object.isRequired,
    cardElementConfiguration: PropTypes.object.isRequired,
  }).isRequired,
  onCardElementCreate: PropTypes.func.isRequired,
  onCardElementUpdate: PropTypes.func.isRequired,
  onCardElementDelete: PropTypes.func.isRequired,
  onElementConfigurationChange: PropTypes.func.isRequired,
};

const CardDesignerLogoControls = ({
  className,
  cardBounds,
  element = {},
  onCardElementCreate,
  onCardElementUpdate,
  onCardElementDelete,
  onElementConfigurationChange,
}) => {
  const classes = classNames({
    [className]: !!className,
  });

  const { cardElementConfiguration } = element;
  const fileInputRef = useRef();
  const [centerLogo, setCenterLogo] = useState(false);
  const { accountLogo } = useAccountLogo();
  const [query, setQuery] = useState('');
  const [debouncedQuery] = useDebounce(query, 500);

  const { printAssets, printAssetsTotal, setSearchQuery } =
    useAccountPrintAssets({
      printAssetType: PRINT_ASSET_TYPES.LOGO,
    });

  useEffect(() => {
    setSearchQuery(debouncedQuery);
  }, [debouncedQuery]);

  // The idea here is to setup the frame to an appropriate size after initial creation
  useEffect(() => {
    if (cardElementConfiguration && centerLogo) {
      const { asset_image: assetImage } = cardElementConfiguration;
      const frame = getImageBasedResizedFrame(
        cardBounds,
        BASE_LOGO_FRAME,
        assetImage,
        true,
      );

      onCardElementUpdate({
        cardElement: element.cardElement,
        data: {
          frame,
        },
      });
      setCenterLogo(false);
    }
  }, [cardElementConfiguration]);

  const { uploading, onUpload, onUploadReset } = useAssetImageUpload({
    onImageUploaded: async assetImage => {
      // Having a cardElementConfiguration means we're working with an actual element
      if (cardElementConfiguration) {
        onElementConfigurationChange({
          element,
          elementConfig: {
            asset_image_id: assetImage.id,
          },
        });
      } else {
        setCenterLogo(true);
        await onCardElementCreate({
          cardElementType: CARD_ELEMENT_TYPE.PHOTO,
          cardElementConfiguration: {
            is_editable: false,
            asset_image_id: assetImage.id,
          },
          input: {
            frame: BASE_LOGO_FRAME,
            z_index: MAX_Z_INDEX,
          },
        });
      }
    },
  });

  const handlePrintAssetSelect = printAsset => {
    if (cardElementConfiguration) {
      onElementConfigurationChange({
        element,
        elementConfig: {
          existing_asset_image_id: printAsset.asset_image.id,
        },
      });
    } else {
      setCenterLogo(true);
      onCardElementCreate({
        cardElementType: CARD_ELEMENT_TYPE.PHOTO,
        cardElementConfiguration: {
          is_editable: false,
          existing_asset_image_id: printAsset.asset_image.id,
        },
        input: {
          frame: BASE_LOGO_FRAME,
          z_index: MAX_Z_INDEX,
        },
      });
    }
  };

  return (
    <div className={classes}>
      <Label id="logo-control">Logo</Label>
      <input
        id="circle-asset-image"
        type="file"
        accept="image/*"
        onChange={event => {
          const [file] = event.target.files;
          onUpload(file);
        }}
        ref={ref => {
          fileInputRef.current = ref;
        }}
        hidden
      />
      <Button
        btnBlock
        btnStyle="info"
        className="text-primary text-sm text-bold"
        disabled={uploading}
        onClick={() => fileInputRef.current?.click()}
      >
        {uploading ? (
          <Fragment>Saving...</Fragment>
        ) : (
          <Fragment>
            <UploadIcon size={12} color="#0d84fe" className="mr-1" />
            Upload an image
          </Fragment>
        )}
      </Button>
      {cardElementConfiguration?.asset_image && (
        <div className="card-designer-logo-controls-logo-container">
          <Button
            btnStyle="reset"
            onClick={async () => {
              onUploadReset();
              await onCardElementDelete(element.cardElement.id);
            }}
          >
            &times;
          </Button>
          <img src={cardElementConfiguration.asset_image.url} alt="Logo" />
        </div>
      )}
      {accountLogo &&
        accountLogo?.id === cardElementConfiguration?.asset_image?.id && (
          <p className="text-light text-sm mt-2">
            Your account logo has been applied. Changing or removing your logo
            here will only update the design and will not overwrite your account
            logo settings.
          </p>
        )}
      {accountLogo &&
        accountLogo?.id !== cardElementConfiguration?.asset_image?.id && (
          <Fragment>
            <Label id="account-logo" className="mt-3">
              Account Logo
            </Label>
            <div className="card-designer-print-assets-container">
              <Button
                className="card-designer-print-assets-item"
                btnStyle="reset"
                onClick={() => {
                  if (cardElementConfiguration) {
                    onElementConfigurationChange({
                      element,
                      elementConfig: {
                        existing_asset_image_id: accountLogo.id,
                      },
                    });
                  } else {
                    setCenterLogo(true);
                    onCardElementCreate({
                      cardElementType: CARD_ELEMENT_TYPE.PHOTO,
                      cardElementConfiguration: {
                        is_editable: false,
                        existing_asset_image_id: accountLogo.id,
                      },
                      input: {
                        frame: BASE_LOGO_FRAME,
                        z_index: MAX_Z_INDEX,
                      },
                    });
                  }
                }}
              >
                <img
                  src={accountLogo.url}
                  alt="Account Logo"
                  className="card-designer-print-backgrounds-assets-image"
                />
              </Button>
            </div>
          </Fragment>
        )}
      {printAssetsTotal > 0 && (
        <Fragment>
          <Label id="logo-assets" className="mt-3">
            Your logo assets
          </Label>
          <FormGroup
            id="query"
            name="query"
            label="Query"
            labelSrOnly
            className="mt-2"
            placeholder="Search your logos.."
            onChange={({ target }) => setQuery(target.value)}
            value={query}
          />

          <div className="card-designer-print-assets-container">
            {printAssets.map(printAsset => (
              <Button
                className="card-designer-print-assets-item"
                btnStyle="reset"
                onClick={() => handlePrintAssetSelect(printAsset)}
              >
                <img
                  src={printAsset.asset_image.url}
                  alt={printAsset.name}
                  className="card-designer-print-backgrounds-assets-image"
                />
              </Button>
            ))}
          </div>
        </Fragment>
      )}
    </div>
  );
};

CardDesignerLogoControls.propTypes = propTypes;

export default CardDesignerLogoControls;
